/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../ts';
import {
  ELASTIC_SEARCH_AUTH_TYPES,
  ELASTIC_SEARCH_INDEX_INPUT_METHODS
} from '../../../ts/skill';
import type { ElasticSlice } from '../../../ts/state';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  RESPONSE_LENGTH_VALUE
} from '../../discovery/util/constants';
import {
  listIndices,
  getElserSupport,
  getIndexMapping,
  createElser,
  ingestDocs,
  listDocs,
  deleteDocs
} from './actions';
import { elasticAdapter } from './adapter';

const initialState: ElasticSlice = elasticAdapter.getInitialState({
  draft: null,
  isDirty: false,
  isEditing: false,
  showDetailConfig: false,
  elasticAuthType: ELASTIC_SEARCH_AUTH_TYPES.BASIC,
  previouslySavedElasticAuthType: null,
  previouslySelectedIndex: null,
  previouslyManagedIndex: null,
  // Set the default index input method to text input. This way if the user is editing a previously configured
  // integration the input field will show the index value and the dropdown will look empty (since it is unless the user
  // chooses to reload their indices).
  elasticIndexInputMethod: ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX,
  isElasticSetupValid: false,
  isElasticConfigValid: false,
  listFieldsStatus: ApiStatus.IDLE,
  listFieldsError: null,
  listFieldExamplesStatus: ApiStatus.IDLE,
  listFieldExamplesError: null,
  listElasticIndicesStatus: ApiStatus.IDLE,
  listElasticIndicesFetched: 0,
  listElasticIndicesError: null,
  isElserSupported: true,
  elserModelId: null,
  isElserSupportedStatus: ApiStatus.IDLE,
  isElserSupportedError: null,
  indices: [],
  indicesStatus: ApiStatus.IDLE,
  indicesError: null,
  fields: [],
  fieldsStatus: ApiStatus.IDLE,
  fieldsError: null,
  fieldExamples: [],
  fieldExamplesStatus: ApiStatus.IDLE,
  fieldExamplesError: null,
  uploadFilesStatus: ApiStatus.IDLE,
  uploadFilesError: null,
  deleteFilesStatus: ApiStatus.IDLE,
  deleteFilesError: null,
  listFiles: [],
  listFilesStatus: ApiStatus.IDLE,
  listFilesError: null,
  contentMapping: [],
  contentMappingStatus: ApiStatus.IDLE,
  contentMappingError: null,
  createElser: false,
  createElserStatus: ApiStatus.IDLE,
  createElserError: null,
  skillId: null
});

// Slice & Reducer
const slice = createSlice({
  name: 'elastic',
  initialState,
  reducers: {
    resetElasticState() {
      return initialState;
    },
    setElasticIndex(state: ElasticSlice, action) {
      state.draft.search_settings.elastic_search.index = action.payload;
      state.isDirty = true;
    },
    setElasticUrl(state: ElasticSlice, action) {
      state.draft.search_settings.elastic_search.url = action.payload;
      state.isDirty = true;
    },
    setElasticPort(state: ElasticSlice, action) {
      state.draft.search_settings.elastic_search.port = action.payload;
      state.isDirty = true;
    },
    setElasticUser(state: ElasticSlice, action) {
      state.draft.search_settings.elastic_search.username = action.payload;
      state.isDirty = true;
    },
    clearElasticUser(state: ElasticSlice) {
      delete state?.draft?.search_settings?.elastic_search?.username;
    },
    setElasticPass(state: ElasticSlice, action) {
      state.draft.search_settings.elastic_search.password = action.payload;
      state.isDirty = true;
    },
    clearElasticPass(state: ElasticSlice) {
      delete state?.draft?.search_settings?.elastic_search?.password;
    },
    clearContentMapping(state: ElasticSlice) {
      state.contentMapping = [];
    },
    setElasticApiKey(state: ElasticSlice, action) {
      state.draft.search_settings.elastic_search.apikey = action.payload;
      state.isDirty = true;
    },
    setElasticManagedIndex(state: ElasticSlice, action) {
      state.draft.search_settings.elastic_search.managed_index = action.payload;
      state.isDirty = true;
    },
    clearElasticManagedIndex(state: ElasticSlice) {
      delete state.draft.search_settings.elastic_search.managed_index;
    },
    setPreviouslyManagedIndex(state: ElasticSlice, action) {
      state.previouslyManagedIndex = action.payload;
      state.isDirty = true;
    },
    clearElasticApiKey(state: ElasticSlice) {
      delete state?.draft?.search_settings?.elastic_search?.apikey;
    },
    setElasticAuthType(state: ElasticSlice, action) {
      state.elasticAuthType = action.payload;
      state.isDirty = true;
    },
    setElasticAuthTypeDefault(state: ElasticSlice) {
      state.elasticAuthType = ELASTIC_SEARCH_AUTH_TYPES.BASIC;
    },
    setPreviouslySavedElasticAuthType(state: ElasticSlice, action) {
      state.previouslySavedElasticAuthType = action.payload;
      state.elasticAuthType = action.payload;
    },
    setPreviouslySelectedIndex(state: ElasticSlice, action) {
      state.previouslySelectedIndex = action.payload;
      state.elasticAuthType = action.payload;
    },
    setElasticIndexInputMethod(state: ElasticSlice, action) {
      state.elasticIndexInputMethod = action.payload;
      state.isDirty = true;
    },
    clearDiscoveryDetails(state: ElasticSlice) {
      delete state?.draft?.search_settings?.discovery;
    },
    setIsElasticSetupValid(state: ElasticSlice, action) {
      state.isElasticSetupValid = action.payload;
    },
    resetListFiles(state: ElasticSlice) {
      state.listFiles = [];
    },
    setIsElasticConfigValid(state: ElasticSlice, action) {
      state.isElasticConfigValid = action.payload;
    },
    setElasticSearchSkillDraft(state: ElasticSlice, action) {
      const { skill, isDirty } = action.payload;
      state.draft = skill || null;
      state.isDirty = isDirty;
    },
    resetIsDirty(state: ElasticSlice) {
      state.isDirty = false;
    },
    setIsEditing(state: ElasticSlice, action) {
      state.isEditing = action.payload;
    },
    setShowDetailConfig(state: ElasticSlice, action) {
      state.showDetailConfig = action.payload;
    },
    clearElserSettings(state: ElasticSlice) {
      state.isElserSupported = true;
      state.elserModelId = null;
      state.isElserSupportedStatus = ApiStatus.IDLE;
      state.isElserSupportedError = null;
    },
    // This reducer does not set isDirty to true because it's just updating defaults. We don't need to confuse tanya by
    // enabling the save button for the defaults, instead the defaults will only be saved if she makes an edit and then
    // chooses to save.
    setDraftConversationalSearchEnabledDefault(state: ElasticSlice) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: false
      };
    },
    setDraftConversationalSearchRetrievalConfidenceThresholdDefault(
      state: ElasticSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchConfidenceThresholdDefault(
      state: ElasticSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchVerbosityOptionDefault(state: ElasticSlice) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: RESPONSE_LENGTH_VALUE.MODERATE
      };
    },
    setDraftConversationalSearchResponseConfidenceThresholdDefault(
      state: ElasticSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchMultiTurnDefault(state: ElasticSlice) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: false
      };
    },
    // This reducer does not set isDirty to true because it's just updating defaults. We don't need to confuse tanya by
    // enabling the save button for the defaults, instead the defaults will only be saved if she makes an edit and then
    // chooses to save.
    setDraftCitationsTitleValueDefault(state: ElasticSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
    },
    setDraftMaxCitationsValueDefault(state: ElasticSlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
    },
    setDraftIsConversationalSearchEnabled(state: ElasticSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchRetrievalConfidenceThreshold(
      state: ElasticSlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchConfidenceThreshold(
      state: ElasticSlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchVerbosityOption(
      state: ElasticSlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchResponseConfidenceThreshold(
      state: ElasticSlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchMultiTurn(
      state: ElasticSlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftCitationsTitleValue(state: ElasticSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
      state.isDirty = true;
    },
    setDraftMaxCitationsValue(state: ElasticSlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
      state.isDirty = true;
    },
    setDraftSuccessMessage(state: ElasticSlice, action) {
      state.draft.search_settings.messages.success = action.payload;
      state.isDirty = true;
    },
    setDraftNoResultsMessage(state: ElasticSlice, action) {
      state.draft.search_settings.messages.no_result = action.payload;
      state.isDirty = true;
    },
    setDraftConnectivityMessage(state: ElasticSlice, action) {
      state.draft.search_settings.messages.error = action.payload;
      state.isDirty = true;
    },
    setDraftTitleOptionValue(state: ElasticSlice, action) {
      state.draft.search_settings.schema_mapping.title = action.payload;
      state.isDirty = true;
    },
    setDraftBodyOptionValue(state: ElasticSlice, action) {
      state.draft.search_settings.schema_mapping.body = action.payload;
      state.isDirty = true;
    },
    setDraftUrlOptionValue(state: ElasticSlice, action) {
      state.draft.search_settings.schema_mapping.url = action.payload;
      state.isDirty = true;
    },
    setDraftElasticFilterValue(state: ElasticSlice, action) {
      // We only want to save the filter if it exists, if it doesn't exist we should remove the field. This enables
      // store to add it's own default filter if there's no filter saved in the elastic_search settings.
      if (action.payload) {
        state.draft.search_settings.elastic_search.filter = action.payload;
      } else {
        delete state.draft?.search_settings?.elastic_search?.filter;
      }
      state.isDirty = true;
    },
    setDraftElasticQueryBodyValue(state: ElasticSlice, action?) {
      // We only want to save the query body if it exists, if it doesn't exist we should remove the field. This enables
      // store to add it's own default query body if there's no query body saved in the elastic_search settings.
      if (action?.payload) {
        state.draft.search_settings.elastic_search.query_body = action.payload;
      } else {
        delete state.draft?.search_settings?.elastic_search?.query_body;
      }
      state.isDirty = true;
    }
  },

  extraReducers: builder => {
    builder
      .addCase(listIndices.pending, (state: ElasticSlice) => {
        state.indicesStatus = ApiStatus.LOADING;
        state.indicesError = null;
      })
      .addCase(
        listIndices.fulfilled,
        (state: ElasticSlice, action: AnyAction) => {
          state.indicesStatus = ApiStatus.SUCCEEDED;
          state.indicesError = null;
          state.indices = action.payload.indices;
          state.elasticIndexInputMethod =
            ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX;
        }
      )
      .addCase(
        listIndices.rejected,
        (state: ElasticSlice, action: AnyAction) => {
          state.indicesStatus = ApiStatus.ERROR;
          state.indicesError = action.error;
        }
      )
      .addCase(getElserSupport.pending, (state: ElasticSlice) => {
        state.isElserSupportedStatus = ApiStatus.LOADING;
        state.isElserSupportedError = null;
      })
      .addCase(
        getElserSupport.fulfilled,
        (state: ElasticSlice, action: AnyAction) => {
          state.isElserSupportedStatus = ApiStatus.SUCCEEDED;
          state.isElserSupportedError = null;
          state.isElserSupported = action.payload.is_elser_supported;
          state.elserModelId = action.payload.model_id;
        }
      )
      .addCase(
        getElserSupport.rejected,
        (state: ElasticSlice, action: AnyAction) => {
          state.isElserSupportedStatus = ApiStatus.ERROR;
          state.isElserSupportedError = action.error;
        }
      )
      .addCase(getIndexMapping.pending, (state: ElasticSlice) => {
        state.contentMappingStatus = ApiStatus.LOADING;
        state.contentMappingError = null;
      })
      .addCase(
        getIndexMapping.fulfilled,
        (state: ElasticSlice, action: AnyAction) => {
          state.contentMappingStatus = ApiStatus.SUCCEEDED;
          state.contentMappingError = null;
          state.contentMapping = action.payload;
        }
      )
      .addCase(
        getIndexMapping.rejected,
        (state: ElasticSlice, action: AnyAction) => {
          state.contentMappingStatus = ApiStatus.ERROR;
          state.contentMappingError = action.error;
        }
      )
      .addCase(ingestDocs.pending, (state: ElasticSlice) => {
        state.uploadFilesStatus = ApiStatus.LOADING;
        state.uploadFilesError = null;
      })
      .addCase(ingestDocs.fulfilled, (state: ElasticSlice) => {
        state.uploadFilesStatus = ApiStatus.SUCCEEDED;
        state.uploadFilesError = null;
      })
      .addCase(
        ingestDocs.rejected,
        (state: ElasticSlice, action: AnyAction) => {
          state.uploadFilesStatus = ApiStatus.ERROR;
          state.uploadFilesError = action.error;
        }
      )
      .addCase(deleteDocs.pending, (state: ElasticSlice) => {
        state.deleteFilesStatus = ApiStatus.LOADING;
        state.deleteFilesError = null;
      })
      .addCase(
        deleteDocs.fulfilled,
        (state: ElasticSlice, action: AnyAction) => {
          state.deleteFilesStatus = ApiStatus.SUCCEEDED;
          state.deleteFilesError = null;
          state.listFiles = action.payload;
        }
      )
      .addCase(
        deleteDocs.rejected,
        (state: ElasticSlice, action: AnyAction) => {
          state.deleteFilesStatus = ApiStatus.ERROR;
          state.deleteFilesError = action.error;
        }
      )
      .addCase(listDocs.pending, (state: ElasticSlice, action: AnyAction) => {
        const noLoad = action.meta?.arg?.noLoad;
        if (!noLoad) {
          state.listFilesStatus = ApiStatus.LOADING;
        }
        state.listFilesError = null;
      })
      .addCase(listDocs.fulfilled, (state: ElasticSlice, action: AnyAction) => {
        state.listFilesStatus = ApiStatus.SUCCEEDED;
        state.listFilesError = null;
        state.listFiles = action.payload;
      })
      .addCase(listDocs.rejected, (state: ElasticSlice, action: AnyAction) => {
        state.listFilesStatus = ApiStatus.ERROR;
        state.listFilesError = action.error;
      })
      .addCase(createElser.pending, (state: ElasticSlice) => {
        state.createElserStatus = ApiStatus.LOADING;
        state.createElserError = null;
      })
      .addCase(
        createElser.fulfilled,
        (state: ElasticSlice, action: AnyAction) => {
          state.createElserStatus = ApiStatus.SUCCEEDED;
          state.createElserError = null;
          state.createElser = action.payload;
          state.draft.search_settings.elastic_search = {
            ...state.draft.search_settings.elastic_search,
            managed_index: action.payload?.index,
            index: action.payload?.index
          };
        }
      )
      .addCase(
        createElser.rejected,
        (state: ElasticSlice, action: AnyAction) => {
          state.createElserStatus = ApiStatus.ERROR;
          state.createElserError = action.error;
        }
      );
  }
});

export const {
  resetElasticState,
  setElasticIndex,
  setElasticManagedIndex,
  clearElasticManagedIndex,
  resetListFiles,
  setElasticUrl,
  setElasticPort,
  setElasticUser,
  clearElasticUser,
  clearContentMapping,
  setElasticPass,
  clearElasticPass,
  setElasticApiKey,
  clearElasticApiKey,
  setElasticAuthType,
  setElasticAuthTypeDefault,
  setPreviouslySavedElasticAuthType,
  setPreviouslySelectedIndex,
  setElasticIndexInputMethod,
  clearDiscoveryDetails,
  setIsElasticSetupValid,
  setIsElasticConfigValid,
  setElasticSearchSkillDraft,
  resetIsDirty,
  setIsEditing,
  setShowDetailConfig,
  clearElserSettings,
  setDraftConversationalSearchEnabledDefault,
  setDraftConversationalSearchRetrievalConfidenceThresholdDefault,
  setDraftConversationalSearchConfidenceThresholdDefault,
  setDraftConversationalSearchVerbosityOptionDefault,
  setDraftConversationalSearchResponseConfidenceThresholdDefault,
  setDraftCitationsTitleValueDefault,
  setDraftMaxCitationsValueDefault,
  setDraftIsConversationalSearchEnabled,
  setDraftConversationalSearchRetrievalConfidenceThreshold,
  setDraftConversationalSearchConfidenceThreshold,
  setDraftConversationalSearchVerbosityOption,
  setDraftConversationalSearchResponseConfidenceThreshold,
  setDraftCitationsTitleValue,
  setDraftMaxCitationsValue,
  setDraftSuccessMessage,
  setDraftNoResultsMessage,
  setDraftConnectivityMessage,
  setDraftTitleOptionValue,
  setDraftBodyOptionValue,
  setDraftUrlOptionValue,
  setDraftElasticFilterValue,
  setDraftElasticQueryBodyValue,
  setDraftConversationalSearchMultiTurnDefault,
  setDraftConversationalSearchMultiTurn
} = slice.actions;

export default slice.reducer;
